import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Construction, HelpCircle, MessageSquare, Settings, Users } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { RoleSwitcher } from "./RoleSwitcher";
import { SignOutDialog } from "./SignOutDialog";

export const ProfileDropdown = () => {
  const { profile, roleAvatar, user, activeRole } = useAuth();

  const initials = useMemo(() => {
    const name = profile?.display_name || "User";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [profile?.display_name]);

  const roleLabel = useMemo(() => {
    if (!activeRole) return "";
    return activeRole
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }, [activeRole]);


  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-md px-2 py-1 cursor-pointer"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={roleAvatar || profile?.avatar_url || undefined} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden sm:flex flex-col items-start min-w-0">
            <span className="text-sm font-medium truncate max-w-[140px]" title={profile?.display_name || undefined}>
              {profile?.display_name || "User"}
            </span>
            {roleLabel && (
              <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                {roleLabel}
              </span>
            )}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 rounded-xl border bg-popover p-1.5 shadow-lg"
      >
        <DropdownMenuLabel className="rounded-md p-2 font-normal">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={roleAvatar || profile?.avatar_url || undefined} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">
                {profile?.display_name || "User"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email || ""}
              </p>
              <NavLink
                to="/profile"
                className="mt-0.5 inline-flex text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
              >
                View your account
              </NavLink>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="mx-1" />
        <DropdownMenuItem
          className="px-2 py-1.5 data-[highlighted]:bg-transparent data-[highlighted]:text-foreground"
          onSelect={(event) => event.preventDefault()}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="text-sm">Switch role</span>
            </div>
            <RoleSwitcher
              variant="dropdown"
              className="ml-auto"
              triggerClassName="h-8 rounded-md"
            />
          </div>
        </DropdownMenuItem>

        <SignOutDialog />

        <DropdownMenuSeparator className="mx-1" />
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuItem
          className="rounded-md px-2 py-1.5 data-[highlighted]:bg-transparent data-[highlighted]:text-foreground"
          onSelect={(event) => event.preventDefault()}
        >
          <ThemeToggle variant="dropdown" />
        </DropdownMenuItem>
<DropdownMenuSeparator className="mx-1" />
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">
          Settings
        </DropdownMenuLabel>
        <DropdownMenuItem
          disabled
          className="rounded-md px-2 py-1.5 gap-2 cursor-not-allowed opacity-60"
        >
          <Settings className="h-4 w-4" />
          <span className="text-sm">Settings</span>
          <Construction className="ml-auto h-3.5 w-3.5 text-yellow-500" />
        </DropdownMenuItem>

        <DropdownMenuSeparator className="mx-1" />
        <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">
          Support
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => window.open("/feedback", "_blank")}
          className="rounded-md px-2 py-1.5 gap-2 cursor-pointer"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="text-sm">Feedback</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled
          className="rounded-md px-2 py-1.5 gap-2 cursor-not-allowed opacity-60"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="text-sm">Help</span>
          <Construction className="ml-auto h-3.5 w-3.5 text-yellow-500" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
